import {
  placementGuide,
  privacyNotice,
  setupMessages,
  visionMessages,
} from "@studystraight/shared-config";
import type { CameraPlacement } from "@studystraight/shared-types";
import type { DetectionQuality } from "@studystraight/vision-core";
import { useCallback, useEffect } from "react";

import type { SessionSetup } from "../../app/navigation.js";
import type { AppSettings } from "../../app/settings.js";
import { useCamera } from "../../hooks/useCamera.js";
import { useVisionTracking, type VisionFailure } from "../../hooks/useVisionTracking.js";
import { CameraPreview } from "../../ui/CameraPreview.js";
import { VisionFailureBanner } from "../../ui/VisionFailureBanner.js";
import { DebugOverlay } from "../debug/DebugOverlay.js";
import { SkeletonCanvas } from "../debug/SkeletonCanvas.js";
import { useDebugRecorder } from "../debug/useDebugRecorder.js";

const PLACEMENTS: readonly CameraPlacement[] = ["FRONTAL", "SIDE"];

export interface SetupScreenProps {
  setup: SessionSetup;
  onSetupChange: (setup: SessionSetup) => void;
  /**
   * Settings dùng ở đây cho hai lựa chọn phải sống qua lần mở app sau (§27): camera trước/sau và
   * panel debug đang mở. Cả hai trước đây là state của screen nên mất sạch mỗi lần điều hướng.
   */
  settings: AppSettings;
  onSettingsChange: (settings: AppSettings) => void;
  onBack: () => void;
  onReady: () => void;
}

function statusHint(
  cameraLive: boolean,
  visionReady: boolean,
  quality: DetectionQuality | null,
  visionError: VisionFailure | null,
): string {
  if (!cameraLive) return "Đang mở camera…";
  // Lỗi phải được kiểm TRƯỚC `visionReady`: đúng ca hỏng trên iOS là ready mãi không lên, và
  // nếu xét theo thứ tự cũ thì người dùng chỉ thấy "đang tải…" cho đến hết đời.
  if (visionError) return visionMessages.failed;
  if (!visionReady) return visionMessages.loading;
  if (!quality) return setupMessages.frameHint;
  if (quality.missing.length > 0) {
    // Nói rõ THIẾU GÌ chứ không chỉ "chưa thấy con": người dùng không đoán được phải dịch máy
    // lên, xuống hay ra xa.
    return quality.missing.includes("leftHip") || quality.missing.includes("rightHip")
      ? setupMessages.missingHips
      : setupMessages.adjustHint;
  }
  if (quality.reason === "LOW_CONFIDENCE") return setupMessages.adjustHint;
  return setupMessages.ready;
}

/** §10 + BLOCKER #1 Option C (chọn placement). */
export function SetupScreen({
  setup,
  onSetupChange,
  settings,
  onSettingsChange,
  onBack,
  onReady,
}: SetupScreenProps): React.JSX.Element {
  const camera = useCamera({
    facingMode: settings.facingMode,
    onFacingModeChange: (facingMode) => onSettingsChange({ ...settings, facingMode }),
  });
  const cameraLive = camera.status === "live";
  const debug = settings.debugPanelOpen;
  const recorder = useDebugRecorder();

  // Ghi ring buffer ở MỌI frame, kể cả khi panel debug đang đóng: người dùng chỉ bật debug SAU
  // khi thấy một hiện tượng lạ — buffer phải đã có 60 giây trước đó, không thì không tune được.
  const onFrame = useCallback(recorder.push, [recorder]);

  const vision = useVisionTracking({
    placement: setup.placement,
    videoRef: camera.videoRef,
    enabled: cameraLive,
    onFrame,
  });

  const startCamera = camera.start;
  useEffect(() => {
    // Chỉ mở camera khi vào screen này (§7: không mở ở Home). Việc đổi camera trước/sau đi qua
    // nút riêng, nên effect này không phụ thuộc facingMode.
    startCamera();
  }, [startCamera]);

  const mismatch = vision.quality?.placementMismatch;
  const canCalibrate = cameraLive && vision.ready && (vision.quality?.ok ?? false);

  return (
    <main className="screen">
      <header className="topbar">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          ← Quay lại
        </button>
        <h2 className="topbar__title">Chuẩn bị camera</h2>
        <button
          type="button"
          className="btn btn--ghost topbar__end"
          aria-pressed={debug}
          onClick={() => onSettingsChange({ ...settings, debugPanelOpen: !debug })}
        >
          {debug ? "Ẩn debug" : "Debug"}
        </button>
      </header>

      {/* Video nằm trong `.stage` để nó nhận phần chiều cao còn lại, còn hướng dẫn và nút bấm
          trong `.setup__panel` thì giữ nguyên kích thước — cả screen vừa một màn hình (§24). */}
      <div className="setup__body">
        <div className="stage">
          <CameraPreview camera={camera}>
            {debug ? (
              <SkeletonCanvas
                getVision={recorder.latestVision}
                minVisibility={vision.config.posture.minLandmarkVisibility}
                mirrored={camera.facingMode === "user"}
              />
            ) : null}
          </CameraPreview>
        </div>

        <div className="setup__panel">
          <p className="hint">
            {statusHint(cameraLive, vision.ready, vision.quality, vision.error)}
          </p>

          <VisionFailureBanner failure={vision.error} onRetry={vision.retry} showDetail={debug} />

          {/* Quyết định #21 — failure mode âm thầm nhất của Option C: chọn sai mode thì mọi metric
              lệch nhưng hệ thống vẫn chạy và vẫn cảnh báo. */}
          {mismatch ? (
            <div className="banner" role="status">
              <span>{setupMessages.placementMismatch}</span>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => onSetupChange({ ...setup, placement: mismatch.suspected })}
              >
                Chuyển sang “{placementGuide[mismatch.suspected].label}”
              </button>
            </div>
          ) : null}

          <section className="placement">
            <h3 className="section-title">Con đặt điện thoại ở đâu?</h3>
            <div className="placement__cards">
              {PLACEMENTS.map((placement) => {
                const guide = placementGuide[placement];
                const selected = setup.placement === placement;
                return (
                  <button
                    key={placement}
                    type="button"
                    className={`card${selected ? " card--selected" : ""}`}
                    aria-pressed={selected}
                    onClick={() => onSetupChange({ ...setup, placement })}
                  >
                    <span className="card__title">
                      {guide.label}
                      {guide.badge ? <span className="card__badge">{guide.badge}</span> : null}
                    </span>
                    <span className="card__hint">{guide.hint}</span>
                    {/* Giảm thiểu DU #3: nói trước yêu cầu thấy hông, thay vì để người dùng thất
                        bại ở bước calibration rồi mới biết. */}
                    {placement === "SIDE" ? (
                      <span className="card__warning">{setupMessages.missingHips}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </section>

          <div className="actions">
            <button type="button" className="btn btn--secondary" onClick={camera.switchCamera}>
              Đổi camera
            </button>
            {/* §10: chỉ cho calibrate khi thấy đủ landmark BẮT BUỘC THEO placement đã chọn. */}
            <button
              type="button"
              className="btn btn--primary"
              disabled={!canCalibrate}
              onClick={onReady}
            >
              Bắt đầu hiệu chỉnh
            </button>
          </div>

          <p className="privacy">{privacyNotice}</p>

          {debug ? (
            <DebugOverlay
              recorder={recorder}
              perf={vision.perf}
              quality={vision.quality}
              config={vision.config}
              engine={vision.capabilities}
            />
          ) : null}
        </div>
      </div>
    </main>
  );
}
